import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { FadeIn } from '../components/ui/FadeIn';
import { AppStoreButtons } from '../components/AppStoreButtons';
import { AudienceShowcase } from '../components/AudienceShowcase';
import { SiteFooter } from '../components/ui/SiteFooter';
import { SiteHeader } from '../components/ui/SiteHeader';
import { appTourSteps, practitionerApplicationUrl } from '../data/appTour';
export function AppLaunchPage() {
  return <div className="min-h-screen bg-ivory font-sans text-charcoal selection:bg-purple-200 selection:text-charcoal" data-id="element-649" data-mp="30u0rzzqsfbtl">
      {/* Navigation */}
      <SiteHeader currentPath="/get-the-app" data-id="element-650" data-mp="3atrmi1fclqto" />

      <main data-id="element-651" data-mp="3aryeahp36yjk">
        {/* Launch hero */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20" data-id="element-652" data-mp="3fl9q6x8ra5ic">
          <div className="max-w-4xl" data-id="element-653" data-mp="2i5nirlgc7ntm">
            <FadeIn data-id="element-654" data-mp="2i4kmsz45qv6u">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-6" data-id="element-655" data-mp="2i39npclnqbm2">
                Now available on iOS and Android
              </p>
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-charcoal leading-[1.05] tracking-tight" data-id="element-656" data-mp="3pns0zkvooszj">
                Your whole wellness team,{' '}
                <span className="italic text-purple-800" data-id="element-657" data-mp="3fyrxbo68l9f0">in your pocket</span>
              </h1>
              <p className="mt-7 max-w-2xl text-lg md:text-xl text-charcoal/70 font-light leading-relaxed" data-id="element-658" data-mp="3by824q584fvi">
                Get matched with verified practitioners, book care that fits
                your life, and track how you're really doing — all in the Fem
                Entity app.
              </p>
            </FadeIn>

            <FadeIn delay={150} data-id="element-659" data-mp="2x178ui7dwcux">
              <AppStoreButtons className="mt-10" data-id="element-660" data-mp="2x2ie6mgh9so5" />
              <p className="mt-5 text-sm text-charcoal/50" data-id="element-661" data-mp="2szlx506l64mb">
                Free to download. Available across Canada, excluding Quebec.
              </p>
            </FadeIn>
          </div>
        </section>

        {/* Two sides of the app */}
        <section aria-labelledby="audiences-heading" className="bg-purple-50 border-y border-purple-100 py-16 md:py-24" data-id="element-662" data-mp="r5cx0uk5nm5i">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-663" data-mp="2ph6c21ffpmqo">
            <FadeIn className="max-w-2xl mb-12" data-id="element-664" data-mp="2pir67pxu73o0">
              <h2 id="audiences-heading" className="font-serif text-3xl md:text-5xl text-charcoal leading-tight" data-id="element-665" data-mp="2bqjisosg0p1z">
                One app, both sides of care
              </h2>
              <p className="mt-5 text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-666" data-mp="33hh4va0b1beh">
                Built for the women seeking support, and for the practitioners
                who provide it.
              </p>
            </FadeIn>

            <AudienceShowcase data-id="element-667" data-mp="2zeu94y0ij6c0" />
          </div>
        </section>

        {/* The journey, in order */}
        <section aria-labelledby="journey-heading" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-id="element-668" data-mp="1j143hmtqwa09">
          <FadeIn data-id="element-669" data-mp="1o0p6q9fy74is">
            <h2 id="journey-heading" className="font-serif text-3xl md:text-5xl text-charcoal leading-tight max-w-2xl" data-id="element-670" data-mp="1o18jzcunl4qh">
              How it works, step by step
            </h2>
          </FadeIn>

          <ol className="mt-14 max-w-3xl border-l border-purple-200" data-id="element-671" data-mp="1pgum9b7yn8c4">
            {appTourSteps.map((step, index) => <li key={step.title} className="relative pl-8 md:pl-12 pb-12 last:pb-0" data-id="element-672" data-mp="1kj1eweux88a9" data-mpi={index}>
                <span aria-hidden="true" className="absolute -left-[13px] top-1 flex h-6 w-6 items-center justify-center rounded-full bg-purple-700 text-[11px] font-semibold text-white" data-id="element-673" data-mp="wrnls6oy48o8">
                  {index + 1}
                </span>
                <h3 className="font-serif text-xl md:text-2xl text-charcoal leading-snug" data-id="element-674" data-mp="11r05wjofn1n4">
                  {step.title}
                </h3>
                <p className="mt-3 text-base text-charcoal/70 leading-[1.8]" data-id="element-675" data-mp="e0cuhofb5tvt">
                  {step.description}
                </p>
              </li>)}
          </ol>
        </section>

        {/* Practitioner path */}
        <section className="bg-purple-100/70 border-y border-purple-200" data-id="element-676" data-mp="3dfyakl0m9aqx">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-677" data-mp="3dg6qkls1cw6c">
            <div className="max-w-2xl" data-id="element-678" data-mp="3m8s51pomwgpd">
              <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-snug" data-id="element-679" data-mp="3m9tejpq04v4z">
                Are you a practitioner?
              </h2>
              <p className="mt-3 text-base text-charcoal/70 leading-relaxed" data-id="element-680" data-mp="s8rpv2b09e">
                Apply to join our vetted network and manage your schedule,
                services, and payments from the same app.
              </p>
            </div>
            <a href={practitionerApplicationUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-charcoal/25 px-7 py-4 text-base font-semibold text-charcoal transition-colors duration-200 ease-out hover:border-purple-700 hover:text-purple-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-purple-100" data-id="element-681" data-mp="916u2p810fcw">
              Apply to the network
              <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-682" data-mp="1pifkmju40rll" />
            </a>
          </div>
        </section>

        {/* Final download */}
        <section className="bg-charcoal py-20 md:py-28 text-center" data-id="element-683" data-mp="2m7wsn5gkepqg">
          <div className="max-w-3xl mx-auto px-6" data-id="element-684" data-mp="2haeqpz0ytary">
            <FadeIn data-id="element-685" data-mp="2h7issap4am3i">
              <h2 className="font-serif text-3xl md:text-5xl text-ivory leading-tight" data-id="element-686" data-mp="2d7i3fv0hcqho">
                Start where you are
              </h2>
              <p className="mt-6 text-lg text-ivory/70 font-light leading-relaxed" data-id="element-687" data-mp="2885gzbditlfm">
                Download the app and take the matching quiz — your first
                practitioner match takes about two minutes.
              </p>
            </FadeIn>
            <AppStoreButtons tone="dark" className="mt-10 justify-center items-center" data-id="element-688" data-mp="yaxctytgvqol" />
          </div>
        </section>
      </main>

      <SiteFooter currentPath="/get-the-app" data-id="element-689" data-mp="137eapkqgxjev" />
    </div>;
}